/**
 * RQB control-element adapters wrapping CMS Core components (DR §5.12).
 *
 * Covers every control element RQB renders so the QueryBuilder doesn't fall back
 * to its default native `<select>` / `<button>` chrome:
 *   - Field selector                          → HierarchicalFieldSelector
 *                                               (tree + search; matches token picker UX)
 *   - Operator, Combinator selectors          → SingleSelectField
 *   - AddRule, AddGroup actions               → core Button with AddIcon
 *   - RemoveRule, RemoveGroup actions         → InlineIconButton with TrashIcon
 */

import React, { useContext } from 'react';
import type {
  ActionProps,
  CombinatorSelectorProps,
  FieldSelectorProps,
  MatchConfig,
  MatchMode,
  MatchModeEditorProps,
  OperatorSelectorProps,
  Option,
  OptionGroup,
  RuleType,
} from 'react-querybuilder';
import { defaultPlaceholderFieldName, defaultPlaceholderOperatorName, update } from 'react-querybuilder';

import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { TrashIcon_Deprecated } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';

import { QueryBuilderContext } from './context';
import type { DateRelation } from './dateRelations';
import {
  applyRelation,
  describeUnofferedRelation,
  fieldOffersOffset,
  offeredRelations,
  relationById,
  relationForLeaf,
} from './dateRelations';
import HierarchicalFieldSelector from './HierarchicalFieldSelector';
import { isCountMatchMode } from './matchModes';
import type { FieldType } from './types';
import { DEFAULT_FIELD_TYPE, VARIANT_HINT_KEY } from './types';
import { UnknownFieldOperatorCell, UnknownFieldPathCell } from './UnknownFieldLeaf';

const MENU_ITEM_PADDING = { paddingTop: 6, paddingBottom: 6 };

function isOptionGroupArray<T extends Option>(
  opts: ReadonlyArray<T> | ReadonlyArray<OptionGroup<T>>
): opts is ReadonlyArray<OptionGroup<T>> {
  return opts.length > 0 && 'options' in opts[0];
}

function flattenOptions<T extends Option>(options: ReadonlyArray<T> | ReadonlyArray<OptionGroup<T>>): T[] {
  if (isOptionGroupArray(options)) {
    return options.flatMap((g) => g.options);
  }
  return [...options];
}

// RQB injects a placeholder option with name '~' (defaultPlaceholderFieldName /
// defaultPlaceholderOperatorName) when autoSelectField/autoSelectOperator is false,
// rendered as "------". We strip it so the dropdown shows only real choices and the
// field reads as empty until the user selects.
const RQB_PLACEHOLDER_NAMES: ReadonlySet<string> = new Set([
  defaultPlaceholderFieldName,
  defaultPlaceholderOperatorName,
]);

function isPlaceholderName(name: unknown): boolean {
  return typeof name === 'string' && RQB_PLACEHOLDER_NAMES.has(name);
}

interface SelectorProps {
  id: string;
  label: string;
  value?: string;
  options: ReadonlyArray<Option> | ReadonlyArray<OptionGroup<Option>>;
  onChange: (value: string) => void;
  className?: string;
  fullWidth?: boolean;
}

const Selector: React.FC<SelectorProps> = ({ id, label, value, options, onChange, className, fullWidth = true }) => {
  // Read whether to surface "Required" markers from the QueryBuilder context.
  // The Formik wrapper sets this to `submitCount > 0`; non-Formik callers can
  // pass their own gate. Keeps controls.tsx Formik-free.
  const { shouldShowErrors } = useContext(QueryBuilderContext);
  const flat = flattenOptions(options).filter((opt) => !isPlaceholderName(opt.name));
  // Treat the RQB placeholder ('~') as an empty value so the dropdown reads as blank
  // until the user picks.
  const displayValue = isPlaceholderName(value) ? '' : (value ?? '');
  const showError = shouldShowErrors && !displayValue;
  return (
    <SingleSelectField<string>
      id={id}
      label={label}
      value={displayValue}
      onChange={(v) => onChange(v ?? '')}
      className={className}
      error={showError}
      helperText={showError ? 'Required' : undefined}
      fullWidth={fullWidth}
    >
      {flat.map((opt) => (
        <MenuItem key={opt.name} value={opt.name} style={MENU_ITEM_PADDING}>
          {opt.label ?? opt.name}
        </MenuItem>
      ))}
    </SingleSelectField>
  );
};

// --- Selector adapters ---

// Field uses a hierarchical, searchable picker (matches the token picker UX).
// Operator + Combinator stay flat — they're small enumerated sets.
//
// A leaf the registry cannot resolve has no label to look up, so the picker rendered its raw
// path in a trigger that opened a tree the path is not in. It states the stored path instead,
// and carries the explanation for the whole leaf.
export const FieldSelector: React.FC<FieldSelectorProps> = (props) => {
  const { isUnknownFieldPath } = useContext(QueryBuilderContext);
  // `rule.field` rather than the `value` prop they mirror: it is the same string, is typed
  // non-optional, and is what the operator cell three rows down already reads.
  if (isUnknownFieldPath(props.rule.field)) {
    return <UnknownFieldPathCell path={props.rule.field} />;
  }
  // The picker's default `qb-field`, which is the id the root row's label points at. A sub-leaf's
  // picker takes one of its own — see `SubFieldSelector`.
  return <HierarchicalFieldSelector {...props} />;
};

/** The field cell as a leaf inside a collection match body needs it.
 *
 *  The same picker as `FieldSelector` minus its interception. A sub-leaf's field is an element
 *  name, which is deliberately not a catalog path — the elements of a collection are per-row and
 *  so cannot be published as `FieldDefinition`s of their own — so `isUnknownFieldPath` answers
 *  "unresolvable" for every correctly-authored sub-leaf, and the root cell's own answer would
 *  render the whole body read-only. The nested builder resolves these against its own
 *  `subproperties` list instead.
 *
 *  It also takes an `id` of its own, which is the collision `HierarchicalFieldSelectorProps#id`
 *  was added for: the picker renders its trigger as a TextField, so `id` is a DOM id and the
 *  label's `htmlFor`, and a match row holds the root's picker and every sub-leaf's inside it.
 *  Off the rule's id rather than its path, because a sub-query is its own builder whose rules are
 *  pathed from the root — two match leaves in one tree both hold a sub-leaf at `[0]`, so a path
 *  would collide between the two bodies while being unique within either. The path is the
 *  fallback for a rule react-querybuilder has not stamped an id onto. */
export const SubFieldSelector: React.FC<FieldSelectorProps> = (props) => (
  <HierarchicalFieldSelector {...props} id={`qb-field-${props.rule.id ?? props.path.join('-')}`} />
);

// Eighteen rows at this padding is over 1,100px, which overflows a laptop viewport and simply
// cuts off the last rows — the emptiness checks being the ones an author is least likely to know
// exist. Capped, the menu scrolls instead.
const RELATION_MENU_MAX_HEIGHT = 420;

/** Value of the entry standing in for a stored comparison the menu cannot offer on this leaf — one
 *  the offer has no phrase for, or one an exact comparison refuses. Disabled, so it can be
 *  displayed but never chosen. */
const UNOFFERED_RELATION_ID = '__unoffered__';

/** The runs of rows the menu separates with a rule: the point comparisons, the amounts before, the
 *  amounts after, and the emptiness checks. Derived from what a row *is* rather than listed, so a
 *  row added to a run joins it. */
const relationBlock = (relation: DateRelation): string => `${relation.shape}:${relation.dir ?? ''}`;

// The rule under the last row of a run. A class rather than MUI's `divider` prop, which the Atomic
// MenuItem does not declare; `border-0` leads because Tailwind preflight is off in this project,
// where a bare `border-solid` lights all four sides at the default medium width.
const BLOCK_END_CLASS = 'border-0 border-b border-solid border-slate-500';

/**
 * One dropdown covering what used to be three controls: the comparison, the direction, and whether
 * the right-hand side is a date itself or an amount away from one. Composing those separately let
 * an author build `after` + `before` — a row that reads like a 32-day window and is true for very
 * nearly every claim. What the comparison is measured *against* is the value cell's target picker.
 *
 * An exact (to-the-minute) leaf is not offered the comparisons an exact comparison refuses; a
 * stored leaf carrying one keeps it on screen as a disabled entry, and the validator reports it.
 *
 * Only offset-capable date fields get it. `time` and type-guarded date fields keep the plain
 * operator dropdown, because a target other than a typed date is not available on them at all.
 */
/** Taken off the operator cell's own props rather than re-spelled: `Schema` is generic over the
 *  field and operator types, and this is exactly the one RQB hands the cell it is rendered from —
 *  which inside a collection match body is the nested builder's, not the root's. */
type OperatorCellSchema = OperatorSelectorProps['schema'];

const DateRelationSelector: React.FC<{ rule: RuleType; path: number[]; schema: OperatorCellSchema }> = ({
  rule,
  path,
  schema,
}) => {
  const { shouldShowErrors } = useContext(QueryBuilderContext);
  const operator = isPlaceholderName(rule.operator) ? '' : rule.operator;
  const offered = offeredRelations(rule.value);
  const stored = operator ? relationForLeaf(operator, rule.value) : null;
  const current = stored && offered.includes(stored) ? stored : null;
  // A stored comparison the menu does not list for this leaf — a `!=` offset from a hand-edited CR
  // import, or an `is` on a leaf that has become exact. It gets an entry of its own so the row
  // states what it actually compares, rather than being snapped to the nearest offered phrase,
  // which would change what it means.
  const unoffered = Boolean(operator) && current === null;
  const showError = shouldShowErrors && !operator;

  /** Write the operator and the value as one update.
   *
   *  A relation names an `(operator, dir, shape)` triple, so choosing one changes both.
   *  RQB's own `handleOnChange` writes a single property, and calling it twice would apply the
   *  second write to the tree the first one replaced — the value would land on a stale query and
   *  silently drop the operator change. Both properties therefore go through one dispatch.
   *
   *  Through the `schema` RQB hands this cell rather than a writer threaded down our own context:
   *  `schema` is per-builder, and `path` is relative to the builder that rendered the row. A
   *  root-scoped writer is correct only while there is one builder, and a collection match leaf
   *  nests a second one over the collection's elements — where a `datetime` element offers these
   *  same relations. Given a sub-query path, a root-scoped writer would rewrite whichever
   *  unrelated top-level leaf happens to sit at it. */
  const writeRelation = (operator: string, value: unknown) => {
    const tree = schema.getQuery();
    schema.dispatchQuery(update(update(tree, 'operator', operator, path), 'value', value, path));
  };

  return (
    <SingleSelectField<string>
      id="qb-operator"
      label="Relation"
      value={unoffered ? UNOFFERED_RELATION_ID : (current?.id ?? '')}
      onChange={(id) => {
        const relation = id ? relationById(id) : undefined;
        if (!relation) return;
        writeRelation(relation.operator, applyRelation(relation, rule.value));
      }}
      error={showError}
      helperText={showError ? 'Required' : undefined}
      // `SingleSelectField` forwards `SelectProps`, and MUI's `SelectProps` carries
      // `MenuProps`, so capping the menu needs no wrapper of its own.
      SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: RELATION_MENU_MAX_HEIGHT } } } }}
      fullWidth
    >
      {[
        ...(unoffered
          ? [
              <MenuItem
                key={UNOFFERED_RELATION_ID}
                value={UNOFFERED_RELATION_ID}
                disabled
                style={MENU_ITEM_PADDING}
                className={BLOCK_END_CLASS}
              >
                {stored?.label ?? describeUnofferedRelation(operator, rule.value)}
              </MenuItem>,
            ]
          : []),
        ...offered.map((relation, index) => {
          const endsBlock = index < offered.length - 1 && relationBlock(offered[index + 1]) !== relationBlock(relation);
          return (
            <MenuItem
              key={relation.id}
              value={relation.id}
              style={MENU_ITEM_PADDING}
              className={endsBlock ? BLOCK_END_CLASS : undefined}
            >
              {relation.label}
            </MenuItem>
          );
        }),
      ]}
    </SingleSelectField>
  );
};

export const OperatorSelector: React.FC<OperatorSelectorProps> = (props) => {
  const { isUnknownFieldPath } = useContext(QueryBuilderContext);
  // The same String fallback the ValueEditor had: without this the operator cell of an
  // unresolvable leaf silently offers String operators for a field nobody can resolve.
  if (isUnknownFieldPath(props.rule.field)) {
    return <UnknownFieldOperatorCell operator={props.rule.operator} />;
  }

  return <SubOperatorSelector {...props} />;
};

/** The operator cell for a sub-leaf, for the same reason `SubFieldSelector` exists, and the cell
 *  the root delegates to once its two interceptions have not fired.
 *
 *  It keeps the date-relation dispatch: `last_datetime` is a `datetime` element and offers the
 *  same relations a catalog date field does. The relation writes through `schema`, which inside a
 *  match body is the nested builder's own — see `DateRelationSelector`. */
export const SubOperatorSelector: React.FC<OperatorSelectorProps> = ({
  value,
  options,
  handleOnChange,
  fieldData,
  rule,
  path,
  schema,
}) => {
  // RQB threads our domain props through `fieldData` (the original Field object).
  const fieldType = (fieldData?.field_type ?? DEFAULT_FIELD_TYPE) as FieldType;
  const hints = fieldData?.hints as Record<string, unknown> | undefined;
  if (fieldOffersOffset(fieldType, Boolean(hints?.[VARIANT_HINT_KEY]))) {
    return <DateRelationSelector rule={rule} path={path} schema={schema} />;
  }
  return <Selector id="qb-operator" label="Operator" value={value} options={options} onChange={handleOnChange} />;
};

/** `match` with `mode` chosen. A count mode needs a number to compare against, so switching into
 *  one seeds a count of 1 where the block holds none — what react-querybuilder's own editor
 *  seeds. */
const withMatchMode = (match: MatchConfig, mode: string): MatchConfig =>
  isCountMatchMode(mode) && typeof match.threshold !== 'number'
    ? { ...match, mode: mode as MatchMode, threshold: 1 }
    : { ...match, mode: mode as MatchMode };

/** The quantifier cell of a leaf that ranges over a collection, in place of its operator.
 *
 *  react-querybuilder's own match-mode editor renders through `schema.controls.valueSelector` and
 *  `schema.controls.valueEditor`, which here are the root's adapters — so without this cell the
 *  quantifier would be the stock native `<select>`, inside a dialog built entirely from CMS Core.
 *
 *  A count mode the field offers (`matchModes.ts` offers them on a collection of records) also
 *  asks how many rows, in a box beside the mode, writing `match.threshold` as a number — `null`
 *  while the box is empty, which `validateConditionsTree` blocks rather than saving as zero. Both
 *  sit in one element, because a match row places each of its five children by position. A count
 *  mode the field does not offer arrives only from a hand-edited import and gets no box: the
 *  compiler refuses the mode itself, whatever the number. */
export const MatchModeSelector: React.FC<MatchModeEditorProps> = ({ match, rule, options, handleOnChange }) => {
  // A stored mode this editor does not offer is shown as its own token rather than left blank.
  // Only a hand-edited import produces one — a count mode on a grouped collection, or a mode a
  // later version stopped offering — and a select handed a value none of its items carry renders
  // an *empty* cell, which reads as "no quantifier set" on a leaf that has one. Stating the token
  // is the same answer the row gave before it was authorable, and it keeps the stored value on
  // screen for the author who has to decide what to replace it with.
  const offered = flattenOptions(options);
  // Off `rule.match`, not off the `match` prop: react-querybuilder defaults that prop to
  // `{mode: 'all'}` for a leaf carrying no match block at all, so reading it would show a
  // quantifier — "all of them" — on the one row where the author has to choose one, while
  // `validateConditionsTree` blocks Save saying the field needs a match mode. Empty instead, which
  // is also what puts the shared "Required" marker on the cell once the form has been submitted.
  const stored = rule.match?.mode;
  const known = offered.some((opt) => opt.name === stored);

  const modeSelector = (
    <Selector
      id="qb-match-mode"
      label="Match"
      value={stored}
      options={known || !stored ? offered : [...offered, { name: stored, label: stored }]}
      onChange={(mode) => handleOnChange(withMatchMode(match, mode))}
    />
  );
  if (!known || !isCountMatchMode(stored)) {
    return modeSelector;
  }

  const { threshold } = match;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_96px] items-start gap-12">
      {modeSelector}
      <TextField<string>
        id="qb-match-threshold"
        label="How many"
        type="number"
        value={typeof threshold === 'number' ? String(threshold) : ''}
        onChange={(v) => handleOnChange({ ...match, threshold: v === '' ? null : Number(v) })}
        fullWidth
      />
    </div>
  );
};

export const CombinatorSelector: React.FC<CombinatorSelectorProps> = ({ value, options, handleOnChange }) => (
  // Combinator is a binary AND/OR — keep it intrinsic-width so it doesn't stretch across the
  // full footer like Field/Operator do. The floating label is wider than either value, though,
  // so the min-width is what stops it rendering as "Com…" over a control sized for "AND".
  <Selector
    id="qb-combinator"
    label="Combinator"
    value={value}
    options={options}
    onChange={handleOnChange}
    fullWidth={false}
    className="min-w-[110px]"
  />
);

// --- Action adapters ---

// RQB threads its `controlClassnames` value into each adapter as a `className`
// prop. Wrapping each action in a span lets us land that class on a real DOM
// element (the underlying CMS Core Button / InlineIconButton don't always
// expose a className that lands on the outer-most node we can target via grid).

export const AddRuleAction: React.FC<ActionProps> = ({ handleOnClick, disabled, className }) => (
  <span className={className}>
    <Button color="primary" onClick={handleOnClick} disabled={disabled} startIcon={<AddIcon />}>
      Add Condition
    </Button>
  </span>
);

export const AddGroupAction: React.FC<ActionProps> = ({ handleOnClick, disabled, className }) => (
  <span className={className}>
    <Button color="primary" onClick={handleOnClick} disabled={disabled} startIcon={<AddIcon />}>
      Add Group
    </Button>
  </span>
);

export const RemoveRuleAction: React.FC<ActionProps> = ({ handleOnClick, disabled, className }) => (
  <span className={className}>
    <InlineIconButton
      icon={TrashIcon_Deprecated}
      onClick={handleOnClick}
      disabled={disabled}
      tooltipTitle="Remove condition"
    />
  </span>
);

export const RemoveGroupAction: React.FC<ActionProps> = ({ handleOnClick, disabled, className }) => (
  // Match the 32px height of the neighbouring Add Condition / Add Group buttons
  // and centre the icon — without this, the InlineIconButton's intrinsic ~21px
  // height bottom-aligns with the buttons but its icon sits ~5px below their
  // text baseline, looking off.
  <span className={`${className ?? ''} inline-flex h-32 items-center`}>
    <InlineIconButton
      icon={TrashIcon_Deprecated}
      onClick={handleOnClick}
      disabled={disabled}
      tooltipTitle="Remove group"
    />
  </span>
);
